import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  endOfDay,
  format,
  isValid,
  lastDayOfMonth,
  lastDayOfQuarter,
  lastDayOfWeek,
  lastDayOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import LangEnum from '../../../../../lang/LangEnum';
import { Locales } from '../../../../../modules/translation/messages';
import { TranslateType } from '../../../../../modules/translation/translationContext.types';
import { localeToDateFns } from '../../../../../utils/dates';
import {
  DateRangeFilterValue,
  DateRangeValue,
  DateShortcut,
} from '../../../../FilterV3/components/DateRangeFilterV2/dateRangeFilter.types';

interface CalculateDateRange {
  finalDate?: Date;
  initialDate?: Date;
}

const formatStep = (step: number, delta: number) =>
  step > 0 ? Math.abs(delta * (step - 1)) : -Math.abs(delta * (step - 1));

const calculateRangeByDays = (
  step: number,
  delta: number,
  currentDate: Date,
): CalculateDateRange => {
  const days = formatStep(step, delta);
  const newDay = addDays(currentDate, days);

  return {
    initialDate: newDay,
    finalDate: addDays(currentDate, days + delta),
  };
};

export function calculateDate(
  value: DateRangeFilterValue,
  step: number,
  shortcut?: DateShortcut,
): CalculateDateRange {
  const currentDate = new Date();
  const formattedStep = Number(step);
  const { finalDate, initialDate } = value as DateRangeValue;

  switch (shortcut) {
    case DateShortcut.DAY:
      const date = addDays(currentDate, formattedStep);
      return {
        initialDate: startOfDay(date),
        finalDate: endOfDay(date),
      };

    case DateShortcut.WEEK:
      const weekDate = addWeeks(currentDate, formattedStep);
      return {
        initialDate: startOfWeek(weekDate),
        finalDate: lastDayOfWeek(weekDate),
      };

    case DateShortcut.MONTH:
      const monthDate = addMonths(currentDate, formattedStep);
      return {
        initialDate: startOfMonth(monthDate),
        finalDate: lastDayOfMonth(monthDate),
      };

    case DateShortcut.QUARTER:
      const quarterDate = addQuarters(currentDate, formattedStep);
      return {
        initialDate: startOfQuarter(quarterDate),
        finalDate: lastDayOfQuarter(quarterDate),
      };

    case DateShortcut.YEAR:
      const dateYear = addYears(currentDate, formattedStep);
      return {
        initialDate: startOfYear(dateYear),
        finalDate: lastDayOfYear(dateYear),
      };

    case DateShortcut.SEVEN_DAYS:
      return calculateRangeByDays(formattedStep, 6, currentDate);

    case DateShortcut.THIRTY_DAYS:
      return calculateRangeByDays(formattedStep, 29, currentDate);

    case DateShortcut.SIXTY_DAYS:
      return calculateRangeByDays(formattedStep, 59, currentDate);

    case DateShortcut.NINETY_DAYS:
      return calculateRangeByDays(formattedStep, 89, currentDate);

    case DateShortcut.TWELVE_MONTHS:
      const delta = 12;
      const months = formatStep(formattedStep, delta);
      const newMonth = addMonths(currentDate, months);

      return {
        initialDate: newMonth,
        finalDate: addMonths(currentDate, months + delta),
      };

    case DateShortcut.ALL_PERIOD:
      return {
        finalDate: undefined,
        initialDate: undefined,
      };

    default:
      return {
        finalDate: finalDate || undefined,
        initialDate: initialDate || undefined,
      };
  }
}

export function getDateLabel(
  value: DateRangeFilterValue,
  step: number,
  locale: Locales,
  translate: TranslateType,
  shortcut?: DateShortcut,
): string {
  const formatLocaleDate = (date?: Date, dateFormat = 'P'): string => {
    if (!date || !isValid(date)) {
      console.error(`invalid date ${date}`);
      return '';
    }
    return format(date, dateFormat, { locale: localeToDateFns[locale] });
  };

  const { finalDate, initialDate } = value as DateRangeValue;

  if (!shortcut && (finalDate || initialDate)) {
    const calculatedDate = calculateDate(value, step, shortcut);
    return `${formatLocaleDate(calculatedDate.initialDate)} - ${formatLocaleDate(calculatedDate.finalDate)}`;
  }

  switch (shortcut) {
    case DateShortcut.DAY:
      return formatLocaleDate(calculateDate(value, step, shortcut).initialDate);

    case DateShortcut.MONTH:
      const monthDate = calculateDate(value, step, shortcut);

      if (!monthDate.initialDate || !isValid(monthDate.initialDate)) {
        return translate(LangEnum.GLOBAL_NONE);
      }

      return format(monthDate.initialDate, `MMMM '${translate(LangEnum.GLOBAL_OF)}' yyyy`, {
        locale: localeToDateFns[locale],
      });

    case DateShortcut.QUARTER:
      const quarterDate = calculateDate(value, step, shortcut);

      if (!quarterDate.initialDate || !isValid(quarterDate.initialDate)) {
        return translate(LangEnum.GLOBAL_NONE);
      }

      return format(quarterDate.initialDate, `QQQQ '${translate(LangEnum.GLOBAL_OF)}' yyyy`, {
        locale: localeToDateFns[locale],
      });

    case DateShortcut.YEAR:
      const yearDate = calculateDate(value, step, shortcut);

      if (!yearDate.initialDate || !isValid(yearDate.initialDate)) {
        return translate(LangEnum.GLOBAL_NONE);
      }

      return format(yearDate.initialDate, 'yyyy', { locale: localeToDateFns[locale] });

    case DateShortcut.WEEK:
    case DateShortcut.SEVEN_DAYS:
    case DateShortcut.THIRTY_DAYS:
    case DateShortcut.SIXTY_DAYS:
    case DateShortcut.NINETY_DAYS:
    case DateShortcut.TWELVE_MONTHS:
      const { initialDate, finalDate } = calculateDate(value, step, shortcut);
      return `${formatLocaleDate(initialDate)} - ${formatLocaleDate(finalDate)}`;

    case DateShortcut.ALL_PERIOD:
      return translate(LangEnum.GLOBAL_DATE_ALL_PERIOD);

    default:
      return '';
  }
}
